import { graphql } from "react-relay";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";

import type { noteTemplateGqlApplyTemplateMutation } from "~/__generated__/noteTemplateGqlApplyTemplateMutation.graphql";
import type { noteTemplateGqlCreateNoteTemplateMutation } from "~/__generated__/noteTemplateGqlCreateNoteTemplateMutation.graphql";
import type { noteTemplateGqlDeleteNoteTemplateMutation } from "~/__generated__/noteTemplateGqlDeleteNoteTemplateMutation.graphql";
import type { noteTemplateGqlDuplicateTemplateMutation } from "~/__generated__/noteTemplateGqlDuplicateTemplateMutation.graphql";
import type {
	noteTemplateGqlSaveTemplateMutation,
	TemplateVisibility,
} from "~/__generated__/noteTemplateGqlSaveTemplateMutation.graphql";
import type { noteTemplateGqlStarNoteTemplateMutation } from "~/__generated__/noteTemplateGqlStarNoteTemplateMutation.graphql";
import type { noteTemplateGqlSubmitTemplateMutation } from "~/__generated__/noteTemplateGqlSubmitTemplateMutation.graphql";
import type { noteTemplateGqlUnstarNoteTemplateMutation } from "~/__generated__/noteTemplateGqlUnstarNoteTemplateMutation.graphql";

const applyTemplateMutation = graphql`
	mutation noteTemplateGqlApplyTemplateMutation(
		$noteId: ID!
		$templateId: ID!
		$makeDefault: Boolean!
		$bypassNoteActivitySignals: Boolean!
	) {
		applyTemplate(
			input: { noteId: $noteId, templateId: $templateId, bypassNoteActivitySignals: $bypassNoteActivitySignals }
		) {
			ok
			note {
				id
				version
			}
		}

		rememberNoteTemplateSelection(noteId: $noteId, templateId: $templateId) @include(if: $makeDefault) {
			ok
			note {
				stream {
					template {
						id
					}
				}
			}
		}
	}
`;

export async function applyTemplate({
	noteId,
	templateId,
	makeDefault = false,
	bypassNoteActivitySignals = false,
}: {
	noteId: string;
	templateId: string;
	makeDefault: boolean;
	bypassNoteActivitySignals?: boolean;
}) {
	const { applyTemplate } = await commitMutationPromise<noteTemplateGqlApplyTemplateMutation>(environment, {
		mutation: applyTemplateMutation,
		variables: {
			noteId,
			templateId,
			makeDefault,
			bypassNoteActivitySignals,
		},
	});

	if (!applyTemplate?.ok) throw new Error("Failed to apply template");
}

const saveTemplateMutation = graphql`
	mutation noteTemplateGqlSaveTemplateMutation(
		$templateId: ID!
		$rawDraftJson: JSON!
		$title: String!
		$tags: [String!]!
		$summary: String!
		$visibility: TemplateVisibility
	) {
		saveNoteTemplate(
			id: $templateId
			rawDraftJson: $rawDraftJson
			title: $title
			tags: $tags
			summary: $summary
			visibility: $visibility
		) {
			template {
				...modalBrowseTemplate @relay(mask: false)
				proseDoc
			}
		}
	}
`;

export async function saveTemplate(template: {
	id: string;
	title: string;
	summary: string;
	tags: string[];
	rawDraftJson: unknown;
	visibility?: TemplateVisibility;
}) {
	const { saveNoteTemplate } = await commitMutationPromise<noteTemplateGqlSaveTemplateMutation>(environment, {
		mutation: saveTemplateMutation,
		variables: {
			templateId: template.id,
			title: template.title,
			summary: template.summary,
			tags: template.tags,
			visibility: template.visibility ?? null,
			rawDraftJson: template.rawDraftJson,
		},
	});

	if (!saveNoteTemplate) throw new Error("Failed to save note template");
	return saveNoteTemplate.template;
}

const createNoteTemplateMutation = graphql`
	mutation noteTemplateGqlCreateNoteTemplateMutation(
		$title: String!
		$summary: String!
		$tags: [String!]!
		$rawDraftJson: JSON!
		$visibility: TemplateVisibility!
		$schemaVersion: Int!
		$parentId: ID
	) {
		createNoteTemplate(
			title: $title
			summary: $summary
			tags: $tags
			rawDraftJson: $rawDraftJson
			visibility: $visibility
			schemaVersion: $schemaVersion
			parentId: $parentId
		) {
			templateEdge {
				node {
					id
					...modalBrowseTemplate @relay(mask: false)
				}
			}
		}
	}
`;

export async function createTemplate(variables: {
	title: string;
	summary: string;
	tags: string[];
	rawDraftJson: { [key: string]: unknown };
	visibility: TemplateVisibility;
	parentId?: string;
	schemaVersion: number;
}): Promise<string> {
	const { createNoteTemplate } = await commitMutationPromise<noteTemplateGqlCreateNoteTemplateMutation>(environment, {
		mutation: createNoteTemplateMutation,
		variables: {
			...variables,
			// Sometimes the schema version isn't there, which means it's the old version, 1
			schemaVersion: variables.schemaVersion ?? 1,
		},
		configs: [
			{
				type: "RANGE_ADD",
				parentID: "viewer",
				edgeName: "templateEdge",
				connectionInfo: [{ key: "Browse_noteTemplates", rangeBehavior: "prepend" }],
			},
			{
				type: "RANGE_ADD",
				parentID: "viewer",
				edgeName: "templateEdge",
				connectionInfo: [
					{ key: "TemplatesSelection_noteTemplates", rangeBehavior: "prepend", filters: { first: null } },
				],
			},
		],
	});

	if (!createNoteTemplate) throw new Error("Failed to create template");
	return createNoteTemplate.templateEdge.node.id;
}

export const deleteTemplateMutation = graphql`
	mutation noteTemplateGqlDeleteNoteTemplateMutation($id: ID!) {
		deleteNoteTemplate(id: $id) {
			ok
			deletedTemplateId
		}
	}
`;

export const deleteNoteTemplate = (id: string) => {
	return commitMutationPromise<noteTemplateGqlDeleteNoteTemplateMutation>(environment, {
		mutation: deleteTemplateMutation,
		variables: {
			id,
		},
		configs: [
			{
				type: "RANGE_DELETE",
				parentID: "viewer",
				connectionKeys: [{ key: "TemplatesSelection_noteTemplates", filters: { first: null } }],
				deletedIDFieldName: "deletedTemplateId",
				pathToConnection: ["viewer", "noteTemplates"],
			},
		],
	});
};

export const setDefaultTemplateMutation = graphql`
	mutation noteTemplateGqlSetDefaultNoteTemplateMutation($id: ID!, $accountWide: Boolean) {
		setDefaultNoteTemplate(id: $id, accountWide: $accountWide) {
			ok
		}
	}
`;

const starNoteTemplateMutation = graphql`
	mutation noteTemplateGqlStarNoteTemplateMutation($id: ID!) {
		starNoteTemplate(templateId: $id) {
			template {
				id
				starred
			}
		}
	}
`;

export async function starNoteTemplate(id: string) {
	const { starNoteTemplate } = await commitMutationPromise<noteTemplateGqlStarNoteTemplateMutation>(environment, {
		mutation: starNoteTemplateMutation,
		variables: { id },
	});

	if (!starNoteTemplate) {
		throw new Error("Failed to star note template");
	}
}

const unstarNoteTemplateMutation = graphql`
	mutation noteTemplateGqlUnstarNoteTemplateMutation($id: ID!) {
		unstarNoteTemplate(templateId: $id) {
			template {
				id
				starred
			}
		}
	}
`;

export async function unstarNoteTemplate(id: string) {
	const { unstarNoteTemplate } = await commitMutationPromise<noteTemplateGqlUnstarNoteTemplateMutation>(environment, {
		mutation: unstarNoteTemplateMutation,
		variables: { id },
	});

	if (!unstarNoteTemplate) {
		throw new Error("Failed to star note template");
	}
}

const duplicateTemplateMutation = graphql`
	mutation noteTemplateGqlDuplicateTemplateMutation($id: ID!, $accountWide: Boolean, $title: String!) {
		duplicateNoteTemplate(id: $id, accountWide: $accountWide, title: $title) {
			ok
			templateEdge {
				node {
					id
					...modalBrowseTemplate @relay(mask: false)
				}
			}
		}
	}
`;

export const duplicateTemplate = async (templateId: string, title = ""): Promise<string | undefined> => {
	const res = await commitMutationPromise<noteTemplateGqlDuplicateTemplateMutation>(environment, {
		mutation: duplicateTemplateMutation,
		variables: {
			id: templateId,
			title,
		},
		configs: [
			{
				type: "RANGE_ADD",
				parentID: "viewer",
				edgeName: "templateEdge",
				connectionInfo: [{ key: "Browse_noteTemplates", rangeBehavior: "prepend" }],
			},
			{
				type: "RANGE_ADD",
				parentID: "viewer",
				edgeName: "templateEdge",
				connectionInfo: [{ key: "TemplatesSelection_noteTemplates", rangeBehavior: "prepend" }],
			},
		],
	});
	if (!res.duplicateNoteTemplate?.templateEdge.node.id) {
		throw new Error("Failed to duplicate template");
	}
	return res.duplicateNoteTemplate.templateEdge.node.id;
};

const submitTemplateMutation = graphql`
	mutation noteTemplateGqlSubmitTemplateMutation($templateId: ID!, $authorInfo: JSON!, $termsAccepted: Boolean!) {
		submitNoteTemplate(templateId: $templateId, authorInfo: $authorInfo, termsAccepted: $termsAccepted) {
			template {
				id
				submission {
					id
					status
				}
			}
			submission {
				id
				status
			}
		}
	}
`;

export async function submitTemplate(templateId: string, authorInfo: object) {
	const { submitNoteTemplate } = await commitMutationPromise<noteTemplateGqlSubmitTemplateMutation>(environment, {
		mutation: submitTemplateMutation,
		variables: { templateId, authorInfo, termsAccepted: true },
	});

	return submitNoteTemplate;
}
